import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoOptions } from '@maskito/core';
import { distinctUntilChanged, Subject, takeUntil, zip } from 'rxjs';
import { ModalErrorGeneralComponent } from '../../../common/modal-error-general/modal-error-general.component';
import { ServiceFormAnamnesisService, ShareDataService } from '@shared/services/index';
import {
  IDataAlegias,
  IDataCirugias,
  IDataEnfermedades,
  IDataEspecialidades,
  IDataMedicamento,
} from '@shared/interfaces/index';
import { AnamnesisService } from '../../../services/anamnesis.service';
import { IDataAnamnesis } from '../../../../interfaces/anamnesis.interface';
import { InputWithChipFormFieldV2Component } from '../../../../shared/components/input-with-chip-form-field-v2/input-with-chip-form-field-v2.component';
import {
  SnackInfoComponent,
  SnackInfoProps,
  SnackInfoType,
} from '../../../../shared/components/snack-info/snack-info.component';
import { TextareaWithSpeechToTextFormFieldComponent } from '../../../../shared/components/textarea-with-speech-to-text-form-field/textarea-with-speech-to-text-form-field.component';
import { SelectSearchFormFieldComponent } from '../../../../shared/components/select-search-form-field/select-search-form-field.component';
import { FichaClinicaStore } from '../../../../shared/stores/ficha-clinica/ficha-clinica.store';
import { fichaClinicaFinalizada } from '../../../../shared/helpers/ficha-clinica.helper';
import { AppPermissions, ValidRoles } from '../../../../shared/enums';
import { HasAccessDirective } from '../../../../shared/directives/has-access/has-access.directive';
import { AuthenticationService } from '../../../services/authentication.service';
import { has } from 'lodash';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FinishAppointmentButtonComponent } from '../../../common/components/finish-appointment-button/finish-appointment-button.component';
import {
  ModalConfirmComponent,
  ModalConfirmProps,
  ModalConfirmType,
} from '../../../../shared/components/modal-confirm/modal-confirm.component';

interface FormFieldConfig<T> {
  initialValue: T | null;
  validators: ValidatorFn[];
  permissionsRequired?: AppPermissions[];
  roleRequired?: ValidRoles[];
}

interface AnamnesisForm {
  temperatura: FormControl<string | null>;
  saturacion: FormControl<string | null>;
  frecuenciaCardiaca: FormControl<string | null>;
  presionArterial: FormControl<string | null>;

  alergias: FormControl<Array<string> | null>;
  enfermedades: FormControl<Array<string> | null>;
  cirugias: FormControl<Array<string> | null>;
  medicamentos: FormControl<Array<string> | null>;

  especialidad: FormControl<string | null>;

  antecedentes: FormControl<string | null>;
  relato: FormControl<string | null>;
  estiloVida: FormControl<string | null>;
}

@Component({
  selector: 'soph-anamnesis',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatDividerModule,
    MatExpansionModule,
    MaskitoDirective,
    MatChipsModule,
    MatAutocompleteModule,
    SelectSearchFormFieldComponent,
    InputWithChipFormFieldV2Component,
    TextareaWithSpeechToTextFormFieldComponent,
    HasAccessDirective,
    MatProgressSpinnerModule,
    FinishAppointmentButtonComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './anamnesis.component.html',
  styleUrl: './anamnesis.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnamnesisComponent implements OnInit, OnDestroy {
  public readonly formReady = model(false);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  protected specialities = signal<IDataEspecialidades[]>([]);
  protected allergies = signal<IDataAlegias[]>([]);
  protected illnesses = signal<IDataEnfermedades[]>([]);
  protected surgeries = signal<IDataCirugias[]>([]);
  protected medicines = signal<IDataMedicamento[]>([]);

  private readonly anamnesis = signal<IDataAnamnesis | null>(null);
  protected readonly hasChanges = signal<boolean>(false);
  private readonly wasSavedOnce = signal<boolean>(false);
  protected readonly isSavingData = signal<boolean>(false);
  protected readonly isAppointmentFinished = signal<boolean>(false);

  protected readonly inputTemperaturaMask: MaskitoOptions = {
    mask: /^\d{0,3}(\.\d{0,2})?$/,
    overwriteMode: 'shift',
    postprocessors: [
      ({ value, selection }) => {
        if (!value) {
          return {
            value: '',
            selection: [0, 0],
          };
        }

        const newValue = `${value} °C`;
        return {
          value: newValue,
          selection: [newValue.length - 3, newValue.length - 3],
        };
      },
    ],
  };

  protected readonly inputSaturacionMask: MaskitoOptions = {
    mask: /^\d{0,3}$/,
    overwriteMode: 'shift',
    postprocessors: [
      ({ value, selection }) => {
        if (!value) {
          return {
            value: '',
            selection: [0, 0],
          };
        }

        const newValue = `${value} %`;
        return {
          value: newValue,
          selection: [newValue.length - 2, newValue.length - 2],
        };
      },
    ],
  };

  protected readonly inputFrecuenciaCardiacaMask: MaskitoOptions = {
    mask: /^\d{0,3}$/,
    overwriteMode: 'shift',
    postprocessors: [
      ({ value, selection }) => {
        if (!value) {
          return {
            value: '',
            selection: [0, 0],
          };
        }

        const newValue = `${value} ppm`;
        return {
          value: newValue,
          selection: [newValue.length - 4, newValue.length - 4],
        };
      },
    ],
  };

  protected readonly inputPresionArterialMask: MaskitoOptions = {
    mask: /^\d{0,3}\/?\d{0,3}$/,
    overwriteMode: 'shift',
    postprocessors: [
      ({ value, selection }) => {
        const cleanedValue = value
          .replace(/ mmHg$/, '')
          .replace('/', '')
          .trim();

        const systolic = cleanedValue.slice(0, 3);
        const diastolic = cleanedValue.slice(3, 6);

        let finalValue = systolic;
        if (diastolic) {
          finalValue += `/${diastolic}`;
        }

        const newValue = finalValue ? `${finalValue} mmHg` : '';

        return {
          value: newValue,
          selection: [
            newValue.length > 3 ? newValue.length - 5 : newValue.length,
            newValue.length > 3 ? newValue.length - 5 : newValue.length,
          ],
        };
      },
    ],
  };

  private readonly formFieldConfigs: Record<string, FormFieldConfig<any>> = {
    temperatura: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_VITALS],
    },
    saturacion: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_VITALS],
    },
    frecuenciaCardiaca: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_VITALS],
    },
    presionArterial: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_VITALS],
    },
    alergias: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_DISEASES],
    },
    enfermedades: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_DISEASES],
    },
    cirugias: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_DISEASES],
    },
    medicamentos: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_DISEASES],
    },
    especialidad: {
      initialValue: null,
      validators: [],
      permissionsRequired: [AppPermissions.EDIT_ANAMNESIS_HISTORYS],
    },
    antecedentes: {
      initialValue: null,
      validators: [Validators.maxLength(1000)],
      roleRequired: [ValidRoles.MEDIC],
    },
    relato: {
      initialValue: null,
      validators: [Validators.required, Validators.maxLength(1000)],
      roleRequired: [ValidRoles.MEDIC],
    },
    estiloVida: {
      initialValue: null,
      validators: [Validators.maxLength(1000)],
      roleRequired: [ValidRoles.MEDIC],
    },
  };

  protected form: FormGroup<AnamnesisForm>;
  protected readonly errorMessage: WritableSignal<{ [key: string]: string }> = signal({});
  private readonly auth: AuthenticationService = inject(AuthenticationService);
  private destroy$ = new Subject<void>();

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _anamnesisDataService: ServiceFormAnamnesisService,
    private readonly _anamnesisService: AnamnesisService,
    private readonly _fichaClinicaStore: FichaClinicaStore,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _sharedDataService: ShareDataService
  ) {
    this.form = this._fb.group({
      temperatura: new FormControl<string | null>(null, Validators.compose([])),
      saturacion: new FormControl<string | null>(null, Validators.compose([])),
      frecuenciaCardiaca: new FormControl<string | null>(null, Validators.compose([])),
      presionArterial: new FormControl<string | null>(null, Validators.compose([])),
      alergias: new FormControl<Array<string> | null>(null, Validators.compose([])),
      enfermedades: new FormControl<Array<string> | null>(null, Validators.compose([])),
      cirugias: new FormControl<Array<string> | null>(null, Validators.compose([])),
      medicamentos: new FormControl<Array<string> | null>(null, Validators.compose([])),
      especialidad: new FormControl<string | null>(null, Validators.compose([])),
      antecedentes: new FormControl<string | null>(
        { value: null, disabled: true },
        Validators.compose([Validators.maxLength(1000)])
      ),
      relato: new FormControl<string | null>(
        { value: null, disabled: true },
        Validators.compose([Validators.required, Validators.maxLength(1000)])
      ),
      estiloVida: new FormControl<string | null>(
        { value: null, disabled: true },
        Validators.compose([Validators.maxLength(1000)])
      ),
    });

    this.form.valueChanges.pipe(distinctUntilChanged()).subscribe((newValues) => {
      this.hasChanges.set(this.checkForChanges(newValues));
    });

    effect(
      () => {
        if (this.wasSavedOnce() && !this.hasChanges() && this.form.valid) {
          this._fichaClinicaStore.updateMedicalHistoryBySection('anamnesis', true);
          this.formReady.set(true);
        }

        this._fichaClinicaStore.hasUnsavedChanges(this.hasChanges());
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.getData();
    this.form.updateValueAndValidity();
    this.auth.userPermissions.pipe(takeUntil(this.destroy$)).subscribe((permissions) => {
      if (!permissions.length) return;
      this.disableControls();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.form.reset();
    this.form.clearValidators();
  }

  private shouldDisableField<T>(config: FormFieldConfig<T>, force: boolean): boolean {
    if (force) return true;
    const lacksPermission = !!config.permissionsRequired
      ? !this.auth.hasAccess(config.permissionsRequired)
      : false;
    const lacksRole = !!config.roleRequired
      ? !this.auth.hasAtLeastOneRole(config.roleRequired)
      : false;

    return lacksPermission || lacksRole;
  }

  private disableControls(forceDisableAll = false) {
    for (const key in this.formFieldConfigs) {
      const config = this.formFieldConfigs[key];
      const control = this.form.controls[key as keyof AnamnesisForm];

      if (!control) continue;
      const shouldDisable = this.shouldDisableField(config, forceDisableAll);

      if (shouldDisable !== control.disabled) {
        shouldDisable ? control.disable() : control.enable();

        this._cdr.detectChanges();
      }
    }
  }

  isBtnGuardarDisabled(): boolean {
    return this.form.invalid || !this.hasChanges();
  }

  public updateErrorMessage(fieldName: string) {
    const control: AbstractControl | null = this.form.get(fieldName);
    const currentErrorMessages = this.errorMessage();

    if (control?.hasError('required')) {
      currentErrorMessages[fieldName] = 'Este campo es obligatorio';
    } else {
      currentErrorMessages[fieldName] = '';
    }

    this.errorMessage.set({ ...currentErrorMessages });
  }

  public isRequired(fieldName: string) {
    const control: AbstractControl | null = this.form.get(fieldName);
    return control?.hasError('required') ?? false;
  }

  public isInvalid(fieldName: string) {
    const control: AbstractControl | null = this.form.get(fieldName);
    return control?.invalid ?? false;
  }

  protected openRevertChangesConfirmation() {
    const dialogRef = this.dialog.open(ModalConfirmComponent, {
      autoFocus: false,
      panelClass: 'custom-confirmation-dialog',
      width: '452px',
      data: {
        type: ModalConfirmType.WARNING,
        description: '¿Confirmas que deseas cancelar sin guardar?',
        config: {
          buttons: [
            { title: 'Volver', action: false },
            { title: 'Confirmar', action: true },
          ],
        },
      } as ModalConfirmProps,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.revertChanges();
      }
    });
  }

  public revertChanges() {
    if (this.anamnesis()) {
      const {
        signosVitales: { temperatura, saturacion, frecuenciaCardiaca, presionArterial },
        ...data
      } = this.anamnesis()!;

      this.form.patchValue({
        ...data,
        temperatura,
        saturacion,
        frecuenciaCardiaca,
        presionArterial,
      });
    } else {
      this.form.reset();
    }
  }

  private checkForChanges(newValues: any): boolean {
    if (this.anamnesis()) {
      const {
        signosVitales: { temperatura, saturacion, frecuenciaCardiaca, presionArterial },
        ...data
      } = this.anamnesis()!;

      const originalData = {
        temperatura: temperatura ? `${temperatura} °C` : null,
        saturacion: saturacion ? `${saturacion} %` : null,
        frecuenciaCardiaca: frecuenciaCardiaca ? `${frecuenciaCardiaca} ppm` : null,
        presionArterial: presionArterial ? `${presionArterial} mmHg` : null,
        ...data,
      };

      const newValuesFormatted = {
        ...newValues,
        temperatura: newValues.temperatura
          ? newValues.temperatura.endsWith('°C')
            ? newValues.temperatura
            : `${newValues.temperatura} °C`
          : null,
        saturacion: newValues.saturacion
          ? newValues.saturacion.endsWith('%')
            ? newValues.saturacion
            : `${newValues.saturacion} %`
          : null,
        frecuenciaCardiaca: newValues.frecuenciaCardiaca
          ? newValues.frecuenciaCardiaca.endsWith('ppm')
            ? newValues.frecuenciaCardiaca
            : `${newValues.frecuenciaCardiaca} ppm`
          : null,
        presionArterial: newValues.presionArterial
          ? newValues.presionArterial.endsWith('mmHg')
            ? newValues.presionArterial
            : `${newValues.presionArterial} mmHg`
          : null,
      };

      return JSON.stringify(newValuesFormatted) !== JSON.stringify(originalData);
    } else {
      const defaultValue = {
        temperatura: null,
        saturacion: null,
        frecuenciaCardiaca: null,
        presionArterial: null,
        alergias: null,
        enfermedades: null,
        cirugias: null,
        medicamentos: null,
        especialidad: null,
        antecedentes: null,
        relato: null,
        estiloVida: null,
      };

      return JSON.stringify(defaultValue) !== JSON.stringify(newValues);
    }
  }

  public onSave(form: FormGroup) {
    if (form.valid) {
      this.isSavingData.set(true);

      const { value } = form;

      this._anamnesisService
        .guardar({
          ...value,
          signosVitales: [
            value.temperatura,
            value.saturacion,
            value.frecuenciaCardiaca,
            value.presionArterial,
          ].filter((vital) => vital !== undefined && vital !== null && vital !== ''),
          idAgendamiento: this._fichaClinicaStore.values.idAgendamiento,
        })
        .subscribe({
          next: () => {
            this.wasSavedOnce.set(true);

            const anamnesisData = {
              signosVitales: {
                temperatura: value.temperatura,
                saturacion: value.saturacion,
                frecuenciaCardiaca: value.frecuenciaCardiaca,
                presionArterial: value.presionArterial,
              },
              ...value,
            };

            this.anamnesis.set(anamnesisData);

            this._sharedDataService.setAnamnesisData(anamnesisData);

            this.form.updateValueAndValidity();

            this._fichaClinicaStore.updateMedicalHistoryBySection('anamnesis', true);
            this.formReady.set(true);

            this.snackBar.openFromComponent(SnackInfoComponent, {
              data: {
                message: 'Los cambios han sido guardados con éxito',
                type: SnackInfoType.SUCCESS,
              } as SnackInfoProps,
            });

            this.isSavingData.set(false);

            this._fichaClinicaStore.changeTabSelected(2);
          },
          error: () => {
            this.snackBar.openFromComponent(SnackInfoComponent, {
              data: {
                message: 'No pudimos guardar los cambios, por favor intenta nuevamente',
                type: SnackInfoType.ERROR,
              } as SnackInfoProps,
            });

            this.isSavingData.set(false);
          },
        });
    } else {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });

      this.snackBar.openFromComponent(SnackInfoComponent, {
        data: {
          message: 'Faltan completar campos requeridos.',
          type: SnackInfoType.WARNING,
        },
        duration: 3000,
      });
    }
  }

  private getData() {
    zip(
      this._anamnesisDataService.getEspecialidades(),
      this._anamnesisDataService.getAlergias(),
      this._anamnesisDataService.getEnfermedades(),
      this._anamnesisDataService.getCirugias(),
      this._anamnesisDataService.getMedicamentos()
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([especialidades, alergias, enfermedades, cirugias, medicamentos]) => {
          this.specialities.set(especialidades);
          this.allergies.set(alergias);
          this.illnesses.set(enfermedades);
          this.surgeries.set(cirugias);
          this.medicines.set(medicamentos);

          if (this._fichaClinicaStore.values.idAgendamiento) {
            this.getAnamnesis();
          }
        },
      });
  }

  private getAnamnesis() {
    this._anamnesisService.obtener(this._fichaClinicaStore.values.idAgendamiento!).subscribe({
      next: (response) => {
        if (response.data) {
          const hasAnyData = Object.keys(response.data).some((key) => {
            if (key === 'signosVitales') {
              return Object.values(response.data.signosVitales).some((vital) => vital !== null);
            } else {
              return has(response.data, key) && response.data[key as keyof IDataAnamnesis] !== null;
            }
          });

          this.wasSavedOnce.set(hasAnyData);
          this.form.reset();

          const {
            signosVitales: { temperatura, saturacion, frecuenciaCardiaca, presionArterial },
            ...data
          } = response.data;

          this.anamnesis.set(response.data);

          this._sharedDataService.setAnamnesisData({
            ...response.data,
            temperatura: response.data.signosVitales.temperatura,
            saturacion: response.data.signosVitales.saturacion,
            frecuenciaCardiaca: response.data.signosVitales.frecuenciaCardiaca,
            presionArterial: response.data.signosVitales.presionArterial,
          });

          this.form.patchValue({
            ...data,
            temperatura: temperatura ? `${temperatura} °C` : null,
            saturacion: saturacion ? `${saturacion} %` : null,
            frecuenciaCardiaca: frecuenciaCardiaca ? `${frecuenciaCardiaca} ppm` : null,
            presionArterial: presionArterial ? `${presionArterial} mmHg` : null,
          });

          const isFormValid = this.form.valid;
          this._fichaClinicaStore.updateMedicalHistoryBySection('anamnesis', isFormValid);
          this.formReady.set(isFormValid);

          this._fichaClinicaStore.state$.pipe(distinctUntilChanged()).subscribe((values) => {
            const isAppointmentFinished = fichaClinicaFinalizada(values.estado || '');
            this.disableControls(isAppointmentFinished);
            this.isAppointmentFinished.set(isAppointmentFinished);
          });
        }
      },
      error: () => {
        this.dialog.open(ModalErrorGeneralComponent, {
          width: '452px',
          disableClose: true,
        });
      },
    });
  }

  private panelStates: { [key: string]: boolean } = {};
  protected shouldExpandPanel(fields: Array<string>): boolean {
    return fields.some((fieldName) => {
      const control = this.form.controls[fieldName as keyof AnamnesisForm];
      const hasErrors = control.errors;
      const isTouched = control.touched;

      if (this.panelStates[fieldName]) {
        return true;
      }

      if (isTouched && hasErrors) {
        this.panelStates[fieldName] = true;
        return true;
      }

      return false;
    });
  }

  protected readonly AppPermissions = AppPermissions;
}
